import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAcceptPostMutation } from '../api/use-accept-post-mutation';
import { useRejectPostMutation } from '../api/use-reject-post-mutation';
import { useModerationCaseDetailsQuery } from '../api/use-moderation-case-details-query';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';
import { formatModerationDateTime } from '../utils/dateFormat';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import PageContainer from './PageContainer';

const MessageImage = styled('img')({
  width: '100%',
  height: 'auto',
  objectFit: 'contain',
  borderRadius: 8,
  display: 'block',
  marginTop: 8,
});

const MessageVideo = styled('video')({
  width: '100%',
  height: 'auto',
  objectFit: 'contain',
  borderRadius: 8,
  display: 'block',
  marginTop: 8,
});

const ActionButton = styled(Button)(({ theme, variant }) => ({
  minWidth: 120,
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: 'white',
  backgroundColor: variant === 'approve' ? theme.palette.success.main : theme.palette.error.main,
  '&:hover': {
    backgroundColor: variant === 'approve' ? theme.palette.success.dark : theme.palette.error.dark,
  },
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: theme.spacing(2, 0),
}));

const MessageDetails = () => {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const { posts, loadPosts } = useAuth();
  const acceptPostMutation = useAcceptPostMutation();
  const rejectPostMutation = useRejectPostMutation();
  const { data: caseDetails = null, isLoading, error } = useModerationCaseDetailsQuery(messageId);

  // Dialog states
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [approvalStep, setApprovalStep] = useState(1);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectionStep, setRejectionStep] = useState(1);
  const [rejectionReason, setRejectionReason] = useState('');
  const [otherReason, setOtherReason] = useState('');

  // Constants
  const REJECTION_REASONS = [
    'Content violates community guidelines',
    'Inappropriate language or behavior',
    'Spam or promotional content',
    'Copyright infringement',
  ];

  // Format date and time as "25 May 2025 5:30pm"
  const formatDateTime = (dateString) => formatModerationDateTime(dateString) || '';

  // Find the message from the queue (for basic info)
  const queueMessage = posts.find((post) => post.id === messageId);

  // Generate message data
  const generateMessageData = () => {
    if (!caseDetails?.data) {
      return null;
    }

    // Handle API response structure: response.data may be { success: true, data: {...} } or just {...}
    const data = caseDetails.data.data || caseDetails.data;
    if (!data) {
      return null;
    }

    const content = data.content || {};
    const flaggedUser = data.flaggedUser || {};
    const mediaType = content.mediaType || 'text';
    const attachments = content.attachments || [];

    return {
      id: messageId,
      user: {
        name: flaggedUser.fullName || 'Unknown User',
        avatar: flaggedUser.avatarUrl || 'https://via.placeholder.com/40',
        followers: flaggedUser.followersCount || 0,
        following: flaggedUser.followingCount || 0,
      },
      type:
        (data.violationTypes || data.violations?.violationTypes || [])
          .map((type) => type.charAt(0).toUpperCase() + type.slice(1))
          .join(', ') || 'Message',
      dateTime:
        data.violationDateTime || data.violations?.violationDateTime
          ? formatDateTime(data.violationDateTime || data.violations.violationDateTime)
          : formatDateTime(new Date().toISOString()),
      messageText: content.text || 'No message text available',
      mediaType: mediaType,
      attachments: attachments,
      violationDescription: data.violationDescription || 'Content under review',
      reportedBy: queueMessage?.reportedBy || 'System Moderation',
      contextMessages: data.relatedContent?.contextMessages || { before: [], after: [] },
    };
  };

  const messageData = generateMessageData();

  // If message is not found, redirect to posts list
  if (!isLoading && !messageData && !queueMessage) {
    navigate('/posts');
    return null;
  }

  const handleApprove = () => {
    setIsApprovalDialogOpen(true);
    setApprovalStep(1);
  };

  const handleReject = () => {
    setIsRejectDialogOpen(true);
    setRejectionStep(1);
    setRejectionReason('');
    setOtherReason('');
  };

  const handleApprovalConfirm = async () => {
    try {
      await acceptPostMutation.mutateAsync(messageId);
      setApprovalStep(2);
      loadPosts();
    } catch (error) {
      console.error('Error accepting message:', error);
    }
  };

  const handleApprovalFinal = () => {
    setIsApprovalDialogOpen(false);
    setApprovalStep(1);
    navigate('/posts');
  };

  const handleRejectConfirm = async () => {
    try {
      const reason = rejectionReason === 'Other' ? otherReason : rejectionReason;
      await rejectPostMutation.mutateAsync({ postId: messageId, reason });
      setRejectionStep(2);
      loadPosts();
    } catch (error) {
      console.error('Error rejecting message:', error);
    }
  };

  const handleRejectFinal = () => {
    setIsRejectDialogOpen(false);
    setRejectionStep(1);
    setRejectionReason('');
    setOtherReason('');
    navigate('/posts');
  };

  const handleDialogClose = () => {
    setIsApprovalDialogOpen(false);
    setIsRejectDialogOpen(false);
    setApprovalStep(1);
    setRejectionStep(1);
    setRejectionReason('');
    setOtherReason('');
  };

  const handleReasonChange = (event) => {
    setRejectionReason(event.target.value);
    if (event.target.value !== 'Other') {
      setOtherReason('');
    }
  };

  const handleOtherReasonChange = (event) => {
    setOtherReason(event.target.value);
  };

  if (isLoading) {
    return (
      <PageContainer title="Message Details">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (error || !messageData) {
    return (
      <PageContainer title="Message Details">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
          <Typography color="error">Error: {error?.message || 'Message not found'}</Typography>
          <Button onClick={() => navigate('/posts')} variant="outlined" sx={{ mt: 2 }}>
            Back to Posts
          </Button>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Message Details">
      <Grid container spacing={4}>
        {/* Left Column - Message Details */}
        <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid #e0e0e0' }, pr: { md: 3 } }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar src={messageData.user.avatar} sx={{ mr: 2, width: 56, height: 56 }}>
                {messageData.user.name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {messageData.user.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {messageData.user.followers} Followers • {messageData.user.following} Following
                </Typography>
              </Box>
            </Box>

            <DetailRow>
              <Typography variant="body1" color="text.secondary">
                Type
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {messageData.type}
              </Typography>
            </DetailRow>

            <DetailRow>
              <Typography variant="body1" color="text.secondary">
                Date & Time
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {messageData.dateTime}
              </Typography>
            </DetailRow>

            <DetailRow>
              <Typography variant="body1" color="text.secondary">
                Message
              </Typography>
              <Box sx={{ textAlign: 'right', maxWidth: '60%' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {messageData.messageText}
                </Typography>
                {messageData.mediaType !== 'text' && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 1,
                      justifyContent: 'flex-end',
                    }}
                  >
                    {messageData.mediaType === 'image' ? (
                      <ImageIcon fontSize="small" color="primary" />
                    ) : (
                      <VideocamIcon fontSize="small" color="primary" />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {messageData.mediaType === 'image' ? 'Image' : 'Video'} attached
                    </Typography>
                  </Box>
                )}
              </Box>
            </DetailRow>

            {messageData.attachments && messageData.attachments.length > 0 && (
              <DetailRow>
                <Typography variant="body1" color="text.secondary">
                  Attachments
                </Typography>
                <Box sx={{ maxWidth: '60%' }}>
                  {messageData.attachments.map((attachment, index) => {
                    const isVideo =
                      attachment.type?.includes('video') ||
                      attachment.url?.match(/\.(mp4|webm|ogg)$/i);
                    const isImage =
                      attachment.type?.includes('image') ||
                      attachment.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                    return (
                      <Box key={index} sx={{ mt: index > 0 ? 2 : 0 }}>
                        {isVideo ? (
                          <MessageVideo controls>
                            <source
                              src={attachment.url || attachment.uri}
                              type={attachment.type || 'video/mp4'}
                            />
                            Your browser does not support the video tag.
                          </MessageVideo>
                        ) : isImage ? (
                          <MessageImage
                            src={attachment.url || attachment.uri}
                            alt={attachment.name || `Attachment ${index + 1}`}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {attachment.name || `Attachment ${index + 1}`}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </DetailRow>
            )}

            <DetailRow>
              <Typography variant="body1" color="text.secondary">
                Reported By
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {messageData.reportedBy}
              </Typography>
            </DetailRow>

            <DetailRow>
              <Typography variant="body1" color="text.secondary">
                Violation Description
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
              >
                {messageData.violationDescription}
              </Typography>
            </DetailRow>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3, width: '100%' }}>
            <ActionButton
              variant="reject"
              startIcon={<CloseIcon />}
              onClick={handleReject}
              sx={{ flex: 1, minWidth: 0 }}
            >
              Reject
            </ActionButton>
            <ActionButton
              variant="approve"
              startIcon={<CheckIcon />}
              onClick={handleApprove}
              sx={{ flex: 1, minWidth: 0 }}
            >
              Approve
            </ActionButton>
          </Box>
        </Grid>

        {/* Right Column - Conversation Context */}
        <Grid item xs={12} md={6} sx={{ pl: { md: 3 } }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Conversation Context
          </Typography>

          <Box
            sx={{
              border: '1px solid rgba(255, 152, 0, 0.3)',
              borderRadius: 1,
              p: 2,
              maxHeight: '600px',
              overflowY: 'auto',
            }}
          >
            {messageData.contextMessages.before.length === 0 &&
            messageData.contextMessages.after.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2 }}
              >
                Conversation context not available
              </Typography>
            ) : (
              <List sx={{ p: 0 }}>
                {/* Messages before */}
                {messageData.contextMessages.before.map((msg) => (
                  <ListItem key={msg.id} sx={{ px: 0, alignItems: 'flex-start', py: 1 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ width: 32, height: 32 }} src={msg.author?.avatarUrl}>
                        {msg.author?.fullName?.charAt(0) || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      sx={{ ml: 1 }}
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {msg.author?.fullName || 'Unknown User'}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {msg.text}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: 'block' }}
                          >
                            {formatDateTime(msg.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}

                {/* Flagged message (highlighted) */}
                <ListItem
                  sx={{
                    px: 0,
                    alignItems: 'flex-start',
                    py: 1,
                    backgroundColor: '#ffebee',
                    borderRadius: 1,
                    border: '2px solid #f44336',
                    my: 1,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32 }} src={messageData.user.avatar}>
                      {messageData.user.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    sx={{ ml: 1 }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {messageData.user.name}
                        </Typography>
                        <FlagIcon color="error" fontSize="small" />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                          {messageData.messageText}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: 'block' }}
                        >
                          {messageData.dateTime}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>

                {/* Messages after */}
                {messageData.contextMessages.after.map((msg) => (
                  <ListItem key={msg.id} sx={{ px: 0, alignItems: 'flex-start', py: 1 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ width: 32, height: 32 }} src={msg.author?.avatarUrl}>
                        {msg.author?.fullName?.charAt(0) || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      sx={{ ml: 1 }}
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {msg.author?.fullName || 'Unknown User'}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {msg.text}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: 'block' }}
                          >
                            {formatDateTime(msg.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Approval Dialog - Step 1: Confirmation Question */}
      <Dialog
        open={isApprovalDialogOpen && approvalStep === 1}
        onClose={handleDialogClose}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: '16px',
            textAlign: 'center',
            maxWidth: '400px',
          },
        }}
      >
        <DialogContent sx={{ pb: 2 }}>
          <Box
            sx={{
              p: 2,
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'warning.main',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto',
            }}
          >
            <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Are you sure you want to approve this message?
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            {messageId}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pt: 0, gap: 2 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            sx={{
              color: 'warning.main',
              borderColor: 'warning.main',
              '&:hover': {
                backgroundColor: 'warning.light',
                borderColor: 'warning.main',
              },
              minWidth: 120,
              height: 48,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprovalConfirm}
            variant="contained"
            sx={{
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
              minWidth: 120,
              height: 48,
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog - Step 2: Success Confirmation */}
      <Dialog
        open={isApprovalDialogOpen && approvalStep === 2}
        onClose={handleDialogClose}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: '16px',
            textAlign: 'center',
            maxWidth: '400px',
          },
        }}
      >
        <DialogContent sx={{ pb: 2 }}>
          <Box
            sx={{
              p: 2,
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'success.main',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto',
            }}
          >
            <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="body2" sx={{ mt: 3, mb: 1 }}>
            You have successfully approved the message
          </Typography>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            {messageId}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pt: 0, px: 3 }}>
          <Button
            onClick={handleApprovalFinal}
            variant="contained"
            sx={{
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 300 },
              height: 48,
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog - Step 1: Reason Selection */}
      <Dialog
        open={isRejectDialogOpen && rejectionStep === 1}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'left', fontWeight: 'bold' }}>Reject Message</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'left',
              mb: 2,
              mt: 1,
            }}
          >
            Before you proceed, please provide a reason for rejecting this message
          </Typography>
          <RadioGroup value={rejectionReason} onChange={handleReasonChange}>
            {REJECTION_REASONS.map((text, index) => (
              <FormControlLabel
                key={index}
                value={text}
                control={<Radio />}
                label={text}
                sx={{ mb: 2, borderBottom: '1px solid', borderBottomColor: 'divider', pb: 2 }}
              />
            ))}
            <FormControlLabel value="Other" control={<Radio />} label="Other" />
          </RadioGroup>
          {rejectionReason === 'Other' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              value={otherReason}
              onChange={handleOtherReasonChange}
              placeholder="Please specify the reason"
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            gap: 1.5,
            px: 3,
            py: 3,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 200 },
              height: 48,
              color: 'warning.main',
              borderColor: 'warning.main',
              '&:hover': {
                backgroundColor: 'warning.light',
                borderColor: 'warning.main',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            disabled={!rejectionReason || (rejectionReason === 'Other' && !otherReason)}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 200 },
              height: 48,
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
              '&.Mui-disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog - Step 2: Confirmation */}
      <Dialog
        open={isRejectDialogOpen && rejectionStep === 2}
        onClose={handleDialogClose}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: '24px',
            textAlign: 'center',
            maxWidth: '400px',
          },
        }}
      >
        <DialogContent sx={{ pb: 2 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'error.main',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto 24px',
            }}
          >
            <CloseIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You have rejected the message
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
            {messageId}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pt: 0, px: 3 }}>
          <Button
            onClick={handleRejectFinal}
            variant="contained"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 200 },
              height: 48,
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default MessageDetails;
